/**
 * One-time "Scheduling, refreshed" announcement shown on the first
 * open of the InterviewManager scheduling wizard.
 *
 * Wraps the generic `AnnouncementCarousel` with the four slides agreed
 * for SCH-52 (plans, scheduling email, self-schedule buffers, smarter
 * rescheduling). Mounted from `ScheduleInterview` and `InterviewActivity`
 * — `useOneTimeAnnouncement` gates first-open via localStorage, and the
 * carousel's `featureLaunchDate`/`accountCreatedDate` gate (SCH-52) hides
 * it for accounts created on/after launch so only existing customers —
 * who lived through the old flow — get the heads-up. The account date is
 * the org-level `account.created_at` from the `me` endpoint, NOT the
 * individual user's join date.
 */
import { useSelector } from 'react-redux';
import {
  AnnouncementCarousel,
  useOneTimeAnnouncement,
  type AnnouncementSlide,
} from '@kula-ai/components';
import {
  BufferSelectionAsset,
  IncludeCalendarInviteAsset,
  SaveAndSendInvitesAsset,
  SelfScheduleSlotSelectionAsset,
} from '@kula-ai/assets';
import { selectCurrUser } from '@/store/bootstrap/selectors';

// Bumped to v2 for the buffer-slide + revamped copy: users who dismissed
// the 3-slide v1 should see the updated 4-slide announcement once.
const STORAGE_KEY = 'scheduling-refreshed-v2';

// 8s per slide for the SCH-52 rollout. Lifted to a constant so it's
// easy to tune without diving into JSX.
const AUTO_SCROLL_MS = 8000;

// SCH-52 GA — accounts created on/after this got the revamped flow from
// day one, so the carousel self-suppresses for them (existing-customer
// gate via the carousel's `isAnnouncementRelevant`).
const FEATURE_LAUNCH_DATE = '2026-06-15';

const SLIDES: AnnouncementSlide[] = [
  {
    media: <SelfScheduleSlotSelectionAsset width="100%" height="100%" />,

    title: 'Interview plans, ready to go',
    description:
      'Zoom host, calendar, private interview, and the self-schedule email template now live in the plan.',
  },
  {
    media: <IncludeCalendarInviteAsset width="100%" height="100%" />,
    title: 'Send an email when scheduling',
    description:
      'You can now send an optional candidate and interviewer email, with an .ics file for every interview',
  },
  {
    media: <BufferSelectionAsset width="100%" height="100%" />,
    title: 'Buffers for self-scheduled interviews',
    description:
      'Add buffers before and after every interview so back-to-backs don’t eat into your prep time.',
  },
  {
    media: <SaveAndSendInvitesAsset width="100%" height="100%" />,
    title: 'Smarter rescheduling',
    description:
      'Updated invites only go out when the time actually changes. Swap an interviewer or tweak a scorecard, and the candidate isn’t notified.',
  },
];

export const SchedulingRefreshedAnnouncement = () => {
  const { shouldShow, markSeen } = useOneTimeAnnouncement(STORAGE_KEY);
  const currUser = useSelector(selectCurrUser);

  if (!shouldShow) return null;

  return (
    <AnnouncementCarousel
      open
      onClose={markSeen}
      headerTitle="Scheduling, revamped"
      headerSubtitle="Less setup work, better candidate emails, interview buffers, and a reschedule flow that stops spamming calendars"
      slides={SLIDES}
      autoScrollIntervalMs={AUTO_SCROLL_MS}
      featureLaunchDate={FEATURE_LAUNCH_DATE}
      accountCreatedDate={currUser?.account?.created_at ?? null}
    />
  );
};
